import { ToolName } from '@debbie/common/dist/agents/index.js';
import keys from '@debbie/common/dist/cache/keys.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { agentNoticesCache, agentProposalsCache, useAgentNoticesByCase, useProposalsByCase, } from '@debbie/cortex/dist/agents/index.js';
import * as api from '@debbie/cortex/dist/api/agents.js';
import { caseVouchersEntity } from '@debbie/cortex/dist/case-vouchers/index.js';
import { casesEntity } from '@debbie/cortex/dist/cases/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { interactionsEntity } from '@debbie/cortex/dist/interactions/index.js';
import { paymentPlansEntity } from '@debbie/cortex/dist/paymentPlans/index.js';
import q from '@debbie/cortex/dist/query.js';
import { updatesEntity } from '@debbie/cortex/dist/updates/index.js';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { errorNotification, successNotification } from '../../utils/errors.js';
import Errors from '../Errors.js';
import Panel from '../Panel.js';
import PlaceholderLoader from '../PlaceholderLoader.js';
import AgentNoticeCard from './AgentNoticeCard.js';
import ProposalCard from './ProposalCard.js';
const Container = styled.div `
  display: flex;
  flex-direction: column;
`;
const invalidateProposalEffects = (toolName, caseId) => {
    switch (toolName) {
        case ToolName.CreateInteraction:
            q.invalidate(keys.write.create(interactionsEntity));
            break;
        case ToolName.CreateCreditorMessage:
            q.invalidate([
                ...keys.write.create(interactionsEntity),
                ...keys.write.create(updatesEntity),
                ...keys.write.modify(casesEntity, caseId),
            ]);
            break;
        case ToolName.CreatePaymentPlan:
            q.invalidate([
                ...keys.write.create(paymentPlansEntity),
                ...keys.write.create(caseVouchersEntity),
                ...keys.write.create(interactionsEntity),
            ]);
            break;
        case ToolName.CreateUpdate:
            q.invalidate([
                ...keys.write.create(updatesEntity),
                ...keys.write.modify(casesEntity, caseId),
                ...keys.write.create(interactionsEntity),
            ]);
            break;
        case ToolName.EndCase:
            q.invalidate([
                ...keys.write.modify(casesEntity, caseId),
                ...keys.write.create(updatesEntity),
                ...keys.write.create(interactionsEntity),
                ...keys.write.create(paymentPlansEntity),
            ]);
            break;
    }
};
const AgentProposals = ({ caseId }) => {
    const lang = useSelector((s) => s.lang);
    const proposalsLoadable = useProposalsByCase(caseId);
    const noticesLoadable = useAgentNoticesByCase(caseId);
    const proposals = h.hasData(proposalsLoadable) ? proposalsLoadable.data : null;
    const notices = h.hasData(noticesLoadable) ? noticesLoadable.data : null;
    const loadErrors = [
        h.isError(proposalsLoadable) ? proposalsLoadable.error.message : null,
        h.isError(noticesLoadable) ? noticesLoadable.error.message : null,
    ].filter((message) => message != null);
    const [decidingId, setDecidingId] = useState(null);
    const decisionInFlight = useRef(false);
    const finishDecision = () => {
        decisionInFlight.current = false;
        setDecidingId(null);
    };
    const runDecision = (id, decision) => {
        if (decisionInFlight.current) {
            return;
        }
        decisionInFlight.current = true;
        setDecidingId(id);
        try {
            decision();
        }
        catch (error) {
            finishDecision();
            throw error;
        }
    };
    // invalidate.modify, not create: create misses item-key reads (the preview), leaving a stale preview after approval.
    const approveMutation = useMutation(api.approveProposal, {
        onSuccess: (data, id) => {
            successNotification(lang, 'update', lang.agentProposals.title);
            agentProposalsCache.invalidate.modify(id);
            invalidateProposalEffects(data.proposal.toolName, caseId);
        },
        onError: (err, id) => {
            agentProposalsCache.invalidate.modify(id);
            errorNotification(lang, err, 'update', lang.agentProposals.title);
        },
        onSettled: finishDecision,
    });
    const rejectMutation = useMutation(api.rejectProposal, {
        onSuccess: (_data, id) => {
            successNotification(lang, 'update', lang.agentProposals.title);
            agentProposalsCache.invalidate.modify(id);
        },
        onError: (err, id) => {
            agentProposalsCache.invalidate.modify(id);
            errorNotification(lang, err, 'update', lang.agentProposals.title);
        },
        onSettled: finishDecision,
    });
    const retryMutation = useMutation(api.retryProposal, {
        onSuccess: (data, id) => {
            successNotification(lang, 'update', lang.agentProposals.title);
            agentProposalsCache.invalidate.modify(id);
            invalidateProposalEffects(data.proposal.toolName, caseId);
        },
        onError: (err, id) => {
            agentProposalsCache.invalidate.modify(id);
            errorNotification(lang, err, 'update', lang.agentProposals.title);
        },
        onSettled: finishDecision,
    });
    const dismissFailedMutation = useMutation(api.dismissFailedProposal, {
        onSuccess: (_data, id) => {
            agentProposalsCache.invalidate.modify(id);
        },
        onError: (err, id) => {
            agentProposalsCache.invalidate.modify(id);
            errorNotification(lang, err, 'update', lang.agentProposals.title);
        },
        onSettled: finishDecision,
    });
    const dismissAgentNoticeMutation = useMutation(api.dismissAgentNotice, {
        onSuccess: (_data, id) => {
            agentNoticesCache.invalidate.modify(id);
        },
        onError: (err, id) => {
            agentNoticesCache.invalidate.modify(id);
            errorNotification(lang, err, 'update', lang.agentProposals.title);
        },
        onSettled: finishDecision,
    });
    if (loadErrors.length === 0 &&
        (h.isLoading(proposalsLoadable) || h.isLoading(noticesLoadable)) &&
        (!proposals || !notices)) {
        return (React.createElement(Panel, { header: lang.agentProposals.title },
            React.createElement(PlaceholderLoader, null)));
    }
    if (loadErrors.length === 0 &&
        (!proposals || proposals.length === 0) &&
        (!notices || notices.length === 0)) {
        return null;
    }
    return (React.createElement(Panel, { header: lang.agentProposals.title },
        React.createElement(Container, null,
            loadErrors.length > 0 && React.createElement(Errors, { errors: loadErrors }),
            (notices ?? []).map((notice) => (React.createElement(AgentNoticeCard, { key: notice.noticeId, notice: notice, loading: decidingId === notice.noticeId &&
                    dismissAgentNoticeMutation.isLoading, disabled: decidingId != null, onDismiss: () => {
                    runDecision(notice.noticeId, () => dismissAgentNoticeMutation.mutate(notice.noticeId));
                } }))),
            (proposals ?? []).map((p) => (React.createElement(ProposalCard, { key: `${p.id}-${p.attempts?.length ?? 0}`, proposal: p, caseId: caseId, inFlight: decidingId != null, approveLoading: decidingId === p.id && approveMutation.isLoading, rejectLoading: decidingId === p.id && rejectMutation.isLoading, retryLoading: decidingId === p.id && retryMutation.isLoading, dismissLoading: decidingId === p.id && dismissFailedMutation.isLoading, onApprove: (edits) => {
                    runDecision(p.id, () => approveMutation.mutate(p.id, edits));
                }, onReject: (reason) => {
                    runDecision(p.id, () => rejectMutation.mutate(p.id, reason));
                }, onRetry: (edits) => {
                    runDecision(p.id, () => retryMutation.mutate(p.id, edits));
                }, onDismiss: () => {
                    runDecision(p.id, () => dismissFailedMutation.mutate(p.id));
                } }))))));
};
export default AgentProposals;
//# sourceMappingURL=index.js.map